import React, { useEffect, useState, useMemo, useRef } from "react";
import styles from "./Style.module.css";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetOutletType, useGetOutlets } from "../../APICalling/useApiQueries";
import { useSticky } from "../../Utils/Helper";
import { Header } from "../../Components/Header/Header";
import { OutletTypes } from "./OutletTypes/OutletTypes";
import { OutletCard } from "../../Components/OutletCard/OutletCard";
import { getSessionStorageValue, removeSessionStorageValue, setSessionStorageValue, STORAGE_KEYS } from "../../Utils/SessionStorage";
import LoaderOutletCard from "../../Components/OutletCard/LoaderOutletCard";
import Placeholder from "../../Components/Placeholder/Placeholder";
import CloseCircleIcon from "../../Components/Icons/CloseCircleIcon";
import { useAppointment } from "../../Context/Appointment/AppointmentContext";
import { useLocation as useLocationContext } from "../../Context/Location/LocationContext";
import { useSchedule } from "../../Context/Schedule/ScheduleContext";
import { useGuests } from "../../Context/Guests/GuestsContext";
import { useValidateBookingSession } from "../../Utils/Hooks/useValidateBookingSession";
import { getCurrentDateString } from "../../Utils/date";

export const Outlets = () => {
  useValidateBookingSession({
    requiredKeys: ["appointment_type"],
  });
  const { appointmentTypeID } = useAppointment();
  const { outletTypeID, setOutletTypeID, setSelectedOutletID, setSelectedOutletContact, setSelectedOutletWhatsapp, selectedOutletID } = useLocationContext();
  const { setSelectedTime } = useSchedule();
  const { resetMembers } = useGuests();

  const routeLocation = useLocation();

  const contactToOutlet = routeLocation.state?.contactToOutlet === true;
  const navigate = useNavigate();
  const { isSticky, sentinelRef } = useSticky();
  const skeletonCount = 8;
  const [initialSelectionDone, setInitialSelectionDone] = useState(false);
  const checkedOutletTypes = useRef(new Set());

  const { selectedDate, setSelectedDate } = useSchedule();

  const outletTypeParams = useMemo(() => ({
    pagination: 0,
    status: 1,
  }), []);

  const { data: outletTypeData, isPending: outletTypeLoading, isSuccess: outletTypesLoaded, isError: outletTypeError } = useGetOutletType(outletTypeParams);
  const outletTypeList = useMemo(() => {
    const list = outletTypeData?.data?.data?.list?.outlet_type;
    return Array.isArray(list) ? list : [];
  }, [outletTypeData]);
  // The status=1 endpoint returns active types. Never query a removed saved type.
  const currentOutletType = outletTypeList.find(
    (type) => String(type.id) === String(outletTypeID)
  ) || outletTypeList[0];
  const currentOutletTypeID = currentOutletType?.id;

  useEffect(() => {
    if (currentOutletTypeID != null && currentOutletTypeID !== outletTypeID) {
      setOutletTypeID(currentOutletTypeID);
    }
  }, [currentOutletTypeID, outletTypeID, setOutletTypeID]);

  // Ensure date is never empty: use context, or restore from session storage, or today's date
  useEffect(() => {
    const storedDate = getSessionStorageValue(STORAGE_KEYS.DATE);

    // If context date is empty but session storage has a valid date, restore to context
    if (!selectedDate && storedDate && storedDate.trim()) {
      setSelectedDate(storedDate);
    }

    // If context has a date, keep it in session storage as backup
    if (selectedDate) {
      setSessionStorageValue(STORAGE_KEYS.DATE, selectedDate);
    }
  }, [selectedDate, setSelectedDate]);

  // Use context date → session storage date → today's date
  const validSelectedDate = selectedDate || getSessionStorageValue(STORAGE_KEYS.DATE) || getCurrentDateString();

  const outletListParams = useMemo(() => ({
    outlet_type: currentOutletTypeID,
    pagination: 0,
    status: 1,
    timing_date: validSelectedDate || '',
    is_image: 1,
  }), [currentOutletTypeID, validSelectedDate]);

  const { data: outletListData, isLoading: loading, isSuccess: outletsLoaded, isError } = useGetOutlets(outletListParams, {
    enabled: !!currentOutletTypeID,
  });
  const outlets = outletListData?.data?.data?.list?.outlets || [];

  useEffect(() => {
    if (initialSelectionDone || !outletTypesLoaded) return;
    if (!currentOutletTypeID) {
      setInitialSelectionDone(true);
      return;
    }
    // An error is not evidence that this outlet type is empty.
    if (!outletsLoaded) return;
    if (outlets.length > 0) {
      setInitialSelectionDone(true);
      return;
    }

    checkedOutletTypes.current.add(String(currentOutletTypeID));
    const nextType = outletTypeList.find(
      (type) => !checkedOutletTypes.current.has(String(type.id))
    );
    if (nextType) {
      setOutletTypeID(nextType.id);
    } else {
      // Every active type is empty. Keep the empty state instead of cycling tabs.
      setInitialSelectionDone(true);
    }
  }, [initialSelectionDone, outletTypesLoaded, currentOutletTypeID, outletsLoaded, outlets.length, outletTypeList, setOutletTypeID]);

  const resolvingInitialSelection = !initialSelectionDone && !outletTypeError && !isError;

  
  const outletSelect = (id, contact, wp) => {
    setSessionStorageValue(STORAGE_KEYS.SELECTED_OUTLET_ID, id);
    setSessionStorageValue(STORAGE_KEYS.SELECTED_OUTLET_CONTACT, contact);
    setSessionStorageValue(STORAGE_KEYS.SELECTED_OUTLET_WP, wp);
    setSelectedOutletID(id);
    setSelectedOutletContact(contact);
    setSelectedOutletWhatsapp(wp);

    if (selectedOutletID !== id) {
      setSelectedTime(null);
      removeSessionStorageValue(STORAGE_KEYS.TIME);
      removeSessionStorageValue(STORAGE_KEYS.SELECTED_SERVICES);
      removeSessionStorageValue(STORAGE_KEYS.SERVICE_COUNT);
      removeSessionStorageValue(STORAGE_KEYS.CART_TOTAL);
      removeSessionStorageValue(STORAGE_KEYS.SERVICE_LIST);
      removeSessionStorageValue(STORAGE_KEYS.THERAPIST_DATA);
      removeSessionStorageValue(STORAGE_KEYS.UPDATED_MEMBERS);
      removeSessionStorageValue(STORAGE_KEYS.ACTIVE_GUEST_ID);
      resetMembers();
    }
    navigate('/select-services');
  };


  useEffect(() => {
  if (loading) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }
  }, [loading]);



  // // REDIRECTION TO HOME WHEN VALUE IS NOT AVAILABLE
  useEffect(() => {
    if (appointmentTypeID == null && !contactToOutlet) {
      navigate('/');
    }  
  }, [
    appointmentTypeID,
    contactToOutlet,
    navigate,
  ]);



  

  return (
    <div className={`${styles.outlets}`}>
      <div className="sticky-sentinel" ref={sentinelRef} aria-hidden="true" />
      <div className={`sticky-header ${isSticky ? "is-sticky" : ""} pb-0`}>
        <Header 
        title={contactToOutlet ? "Contact Outlet" : "Select Outlet"}
        noCross={true}
        />
        <OutletTypes
          outletTypeList={outletTypeList}
          loading={outletTypeLoading}
          onSelect={() => setInitialSelectionDone(true)}
        />
      </div>

      <div className="container-md space-y space-x">
        {loading || outletTypeLoading || resolvingInitialSelection ? (
          <LoaderOutletCard
            skeletonCount={skeletonCount}
            customClass={styles.outletCardLoader}
          />
        ) : (outletTypeError && !outletTypeData) || (isError && !outletListData) ? (
          <Placeholder
            heading="Unable to load outlets"
            para="Please try again later"
            icon={<CloseCircleIcon />}
          />
        ) : outlets?.length > 0 ? (
          outlets.map((outlet, index) => {
            return (
              <div key={outlet?.id}>
                <OutletCard
                  loading={loading}
                  contactToOutlet={contactToOutlet}
                  image={outlet?.image}
                  name={outlet?.name}
                  address={outlet?.address}
                  timing={outlet?.timing_date}
                  // isOpen24={outlet?.is_open_24}
                  isOpen24={false}
                  webComingSoon={outlet?.web_coming_soon}
                  contactNumber={outlet?.contact_number}
                  whatsappNumber={outlet?.whatsapp_number}
                  announcements={outlet?.announcements}
                  id={outlet?.id}
                  selected={selectedOutletID === outlet?.id}
                  handleSelect={() =>
                    outletSelect(
                      outlet?.id,
                      outlet?.contact_number,
                      outlet?.whatsapp_number
                    )
                  }
                />

                {index < outlets.length - 1 ? (
                  <hr className="line" />
                ) : (
                  <>
                    <br />
                    <br />
                  </>
                )}
              </div>
            );
          })
        ) : (
          <Placeholder
            heading="Currently no outlet is available"
            para="Please select another outlet type or try again later"
            icon={<CloseCircleIcon />}
          />
        )}
      </div>
    </div>
  );
};
